<template>
  <div ref="filterItem" class="markets-filter" @click="handleItemClick">
    <slot>
      <p class="markets-filter-label">
        {{ translateCurrentFilter(item) }}
      </p>
      <p v-if="item" class="markets-filter-title" data-t="filter-title">
        <span v-for="(headName, i) in item.header" :key="i">
          {{ getHeadName(headName) }}
        </span>
      </p>
    </slot>
    <!-- dropdown with filters -->
    <div v-if="isActive" class="markets-filter-group" data-t="filter-group">
      <div
        v-for="filter in filteredList"
        :key="filter.id"
        class="markets-filter-item"
        :class="{ active: activeId === filter.id }"
        data-t="filter-group-item"
        @click="choose(filter.id)"
      >
        <p>{{ localizeEventFilters(filter.titlei18n) }}</p>
        <StIcon v-if="activeId === filter.id" :size="8" name="check" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type {
  FilterConfig,
  FilterConfigI18nTitles,
} from 'markets-store/marketsbook/types'
import { onClickOutside } from '@st/use/composables'
import { useLocalizeEventFilters } from '../../composables'

interface Props {
  item: FilterConfig | null
  list: FilterConfig[]
  index: number
  activeId: string | null
}

const props = withDefaults(defineProps<Props>(), {
  list: () => [],
  index: 0,
  activeId: null,
})

const emit = defineEmits<{
  'handle-choose': [params: { index: number; id: string }]
}>()
const localizeEventFilters = useLocalizeEventFilters()

type ListMap = Record<string, FilterConfig>
const filteredList = computed(() => {
  const hashList = props.list.reduce<ListMap>((acc, item) => {
    acc[item.id] = item
    return acc
  }, {})

  return Object.values(hashList)
})

const filterItem = ref<HTMLDivElement>()
const isActive = ref(false)

onClickOutside(filterItem, () => {
  if (isActive.value) isActive.value = false
})

function translateCurrentFilter(currentFilter: FilterConfig | null) {
  return currentFilter ? localizeEventFilters(currentFilter.titlei18n) : ' - '
}
function getHeadName(headName: string) {
  const regx = /[a-z]/

  if (regx.test(headName) && headName !== '1x' && headName !== 'x2') {
    return localizeEventFilters(headName as FilterConfigI18nTitles)
  }

  return headName
}

function choose(id: string) {
  emit('handle-choose', { index: props.index, id })
}
function handleItemClick() {
  /* выпадающий список пока отключен */
  // isActive.value = !isActive.value
  isActive.value = false
}
</script>

<style scoped>
.markets-filter-label {
  display: flex;
  align-items: center;
  justify-content: center;

  margin: 0;

  font: var(--desktop-text-xxs-semibold);
  color: var(--text-tertiary);
  text-align: center;
}

.markets-filter-title {
  display: flex;
  gap: var(--spacing-050);
  justify-content: space-between;

  width: 100%;
  margin: 0;

  font: var(--desktop-text-xs-semibold);
  color: var(--text-primary);

  span {
    flex: 1 0 0;
    text-align: center;
    text-transform: uppercase;
  }
}

.markets-filter {
  user-select: none;

  position: relative;

  display: flex;
  flex: 1 0 0;
  flex-direction: column;
  gap: var(--spacing-050);
  align-items: center;

  box-sizing: border-box;

  &:not(:last-child)::after {
    content: '';

    position: absolute;
    top: 0;
    right: calc(var(--spacing-200) * -1);
    bottom: 0;

    display: flex;

    width: 2px;
    margin: var(--spacing-050) 0;

    background: var(--border-secondary);
  }
}

.markets-filter-group {
  position: absolute;
  z-index: 5;
  top: 0;
  right: 0;
  left: 0;

  display: flex;
  flex-direction: column;

  padding: var(--spacing-050);

  background-color: var(--background-secondary);
  border-radius: var(--border-radius-050);
}

.markets-filter-item {
  cursor: pointer;

  display: flex;
  gap: var(--spacing-075);
  align-items: center;
  justify-content: center;

  padding: var(--spacing-050);

  color: var(--text-primary);

  border-radius: var(--border-radius-050);

  p {
    margin: 0;
    font: var(--desktop-text-xs-medium);
  }

  &.active {
    color: var(--text-link);
  }

  &:hover {
    color: var(--text-primary);
    background: var(--background-tertiary);
  }
}
</style>
